/**
 * Print Job Service
 *
 * Orchestrates label generation from templates + asset data.
 * Creates print jobs in the database for audit/traceability.
 * Manages the full print lifecycle: create -> generate -> send -> log.
 */
import { supabase } from '../../lib/supabase';
import type { Asset } from '../../types';
import type {
  BarcodeOptions,
  BoxOptions,
  FieldBlockOptions,
  ImageOptions,
  InverseBoxOptions,
  LabelTemplate,
  LineOptions,
  PrintAssetSelection,
  PrintJob,
  PrintJobStatus,
  PrintTransportType,
  QRCodeOptions,
  TextOptions,
} from '../../types/printing';
import { ZPLLabelBuilder } from './ZPLLabelBuilder';

// ---- Template Variable Substitution ----

function substituteVariables(
  content: string | undefined,
  variables: Record<string, string>
): string {
  if (!content) return '';
  let result = content;
  for (const [key, value] of Object.entries(variables)) {
    result = result.replaceAll(key, value);
  }
  return result;
}

function buildVariables(
  asset: Asset,
  options?: {
    jobNumber?: string;
    printedBy?: string;
    companyName?: string;
  }
): Record<string, string> {
  const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
  const printDate = new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });

  return {
    '{AssetType}': asset.type || 'N/A',
    '{SerialNumber}': asset.serial_number || 'N/A',
    '{TagId}': asset.tag_id,
    '{CustomerName}': asset.customer?.name || 'N/A',
    '{Status}': asset.status,
    '{QRUrl}': `${appUrl}/asset/${asset.tag_id}`,
    '{PrintDate}': printDate,
    '{JobNumber}': options?.jobNumber || '',
    '{PrintedBy}': options?.printedBy || '',
    '{CompanyName}': options?.companyName || 'DigniCRM',
    '{CreatedDate}': new Date(asset.created_at).toLocaleDateString(),
  };
}

// ---- ZPL Generation ----

export function generateZPLForAsset(
  asset: Asset,
  template: LabelTemplate,
  options?: {
    jobNumber?: string;
    printedBy?: string;
    companyName?: string;
  }
): string {
  const variables = buildVariables(asset, options);
  const builder = new ZPLLabelBuilder(template.width_dots, template.height_dots, template.rotation);

  for (const elem of template.elements) {
    const content = substituteVariables(elem.content, variables);

    switch (elem.type) {
      case 'text':
        builder.addText(content, elem.options as TextOptions);
        break;
      case 'fieldblock':
        builder.addFieldBlock(content, elem.options as FieldBlockOptions);
        break;
      case 'barcode':
        builder.addBarcode(content, elem.options as BarcodeOptions);
        break;
      case 'qrcode':
        builder.addQRCode(content, elem.options as QRCodeOptions);
        break;
      case 'image':
        builder.addImage(content, elem.options as ImageOptions);
        break;
      case 'box':
        builder.addBox(elem.options as BoxOptions);
        break;
      case 'line':
        builder.addLine(elem.options as LineOptions);
        break;
      case 'inverse-box':
        builder.addInverseBox(elem.options as InverseBoxOptions);
        break;
    }
  }

  return builder.getZPL();
}

export function generateZPLForBatch(
  selections: PrintAssetSelection[],
  template: LabelTemplate,
  options?: {
    jobNumber?: string;
    printedBy?: string;
    companyName?: string;
  }
): { fullZpl: string; itemZpls: Map<string, string> } {
  const itemZpls = new Map<string, string>();
  const zplParts: string[] = [];

  for (const { asset, quantity } of selections) {
    const singleZpl = generateZPLForAsset(asset, template, options);
    itemZpls.set(asset.id, singleZpl);

    for (let i = 0; i < quantity; i++) {
      zplParts.push(singleZpl);
    }
  }

  return {
    fullZpl: zplParts.join('\n'),
    itemZpls,
  };
}

// ---- HTML Preview Generation ----

export function generatePreviewElements(
  asset: Asset,
  template: LabelTemplate,
  options?: {
    jobNumber?: string;
    printedBy?: string;
    companyName?: string;
  }
) {
  const variables = buildVariables(asset, options);
  const builder = new ZPLLabelBuilder(template.width_dots, template.height_dots, template.rotation);

  for (const elem of template.elements) {
    const content = substituteVariables(elem.content, variables);

    switch (elem.type) {
      case 'text':
        builder.addText(content, elem.options as TextOptions);
        break;
      case 'fieldblock':
        builder.addFieldBlock(content, elem.options as FieldBlockOptions);
        break;
      case 'barcode':
        builder.addBarcode(content, elem.options as BarcodeOptions);
        break;
      case 'qrcode':
        builder.addQRCode(content, elem.options as QRCodeOptions);
        break;
      case 'image':
        builder.addImage(content, elem.options as ImageOptions);
        break;
      case 'box':
        builder.addBox(elem.options as BoxOptions);
        break;
      case 'line':
        builder.addLine(elem.options as LineOptions);
        break;
      case 'inverse-box':
        builder.addInverseBox(elem.options as InverseBoxOptions);
        break;
    }
  }

  return builder.getElements();
}

// ---- Print Job CRUD ----

export async function createPrintJob(params: {
  selections: PrintAssetSelection[];
  template: LabelTemplate;
  transport: PrintTransportType;
  printerName?: string;
  printerAddress?: string;
  printedBy?: string;
  companyName?: string;
}): Promise<PrintJob> {
  const { selections, template, transport, printerName, printerAddress, printedBy, companyName } =
    params;

  const totalLabels = selections.reduce((sum, s) => sum + s.quantity, 0);

  // Create the job first to get the job number
  const { data: jobData, error: jobError } = await supabase
    .from('print_jobs')
    .insert({
      job_number: '', // trigger will generate
      status: 'pending' as PrintJobStatus,
      label_template_id: template.id,
      transport,
      printer_name: printerName || null,
      printer_address: printerAddress || null,
      total_labels: totalLabels,
      labels_printed: 0,
    })
    .select()
    .single();

  if (jobError) throw new Error(`Failed to create print job: ${jobError.message}`);

  const job = jobData as unknown as PrintJob;

  // Generate ZPL with the job number
  const { fullZpl, itemZpls } = generateZPLForBatch(selections, template, {
    jobNumber: job.job_number,
    printedBy,
    companyName,
  });

  // Create job items
  const items = selections.map((sel, index) => ({
    print_job_id: job.id,
    asset_id: sel.asset.id,
    quantity: sel.quantity,
    generated_zpl: itemZpls.get(sel.asset.id) || null,
    sort_order: index,
  }));

  const { error: itemsError } = await supabase.from('print_job_items').insert(items);

  if (itemsError) throw new Error(`Failed to create print job items: ${itemsError.message}`);

  // Update job with generated ZPL
  const { data: updatedJob, error: updateError } = await supabase
    .from('print_jobs')
    .update({ generated_zpl: fullZpl })
    .eq('id', job.id)
    .select()
    .single();

  if (updateError) throw new Error(`Failed to update print job ZPL: ${updateError.message}`);

  return updatedJob as PrintJob;
}

export async function updatePrintJobStatus(
  jobId: string,
  status: PrintJobStatus,
  options?: {
    labelsPrinted?: number;
    errorMessage?: string;
    printerName?: string;
    printerAddress?: string;
  }
): Promise<void> {
  const update: Record<string, unknown> = { status };

  if (options?.labelsPrinted !== undefined) {
    update.labels_printed = options.labelsPrinted;
  }
  if (options?.errorMessage) {
    update.error_message = options.errorMessage;
  }
  if (options?.printerName) {
    update.printer_name = options.printerName;
  }
  if (options?.printerAddress) {
    update.printer_address = options.printerAddress;
  }
  if (status === 'completed' || status === 'failed') {
    update.completed_at = new Date().toISOString();
  }

  const { error } = await supabase.from('print_jobs').update(update).eq('id', jobId);

  if (error) throw new Error(`Failed to update print job: ${error.message}`);
}

export async function fetchPrintJobs(options?: {
  limit?: number;
  status?: PrintJobStatus;
}): Promise<PrintJob[]> {
  let query = supabase.from('print_jobs').select('*').order('created_at', { ascending: false });

  if (options?.status) {
    query = query.eq('status', options.status);
  }
  if (options?.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;
  if (error) throw new Error(`Failed to fetch print jobs: ${error.message}`);

  return (data as PrintJob[]) || [];
}

export async function fetchPrintJobWithItems(jobId: string): Promise<PrintJob | null> {
  const { data: job, error: jobError } = await supabase
    .from('print_jobs')
    .select('*')
    .eq('id', jobId)
    .single();

  if (jobError) return null;

  const { data: items } = await supabase
    .from('print_job_items')
    .select(
      '*, asset:assets(id, tag_id, serial_number, type, status, customer:customers(id, name))'
    )
    .eq('print_job_id', jobId)
    .order('sort_order');

  return {
    ...(job as PrintJob),
    items: (items as unknown as PrintJob['items']) || [],
  };
}
